'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import OptimizedImage from '@/components/optimized-media/OptimizedImage';
import { useTranslation } from "@/hooks/useTranslation";

export default function GallerySection() {
    const { t, isRTL } = useTranslation("all-on-4");

    const beforeAfterGallery = [
        {
            before: '/images/treatments/all-on-4/gallery-1-before.webp',
            after: '/images/treatments/all-on-4/gallery-1-after.webp',
            description: t("gallery.descriptions.transformation")
        }
    ];

    const current = beforeAfterGallery[0];

    const containerRef = useRef<HTMLDivElement>(null);
    const [position, setPosition] = useState(50);
    const [isDragging, setIsDragging] = useState(false);

    const updateFromClientX = useCallback((clientX: number) => {
        const el = containerRef.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        const raw = ((clientX - rect.left) / rect.width) * 100;
        const clamped = Math.max(0, Math.min(100, raw));
        setPosition(isRTL ? 100 - clamped : clamped);
    }, [isRTL]);

    useEffect(() => {
        if (!isDragging) return;
        const onMove = (e: MouseEvent | TouchEvent) => {
            const x = 'touches' in e ? e.touches[0].clientX : (e as MouseEvent).clientX;
            updateFromClientX(x);
        };
        const onUp = () => setIsDragging(false);
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
        window.addEventListener('touchmove', onMove);
        window.addEventListener('touchend', onUp);
        return () => {
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onUp);
            window.removeEventListener('touchmove', onMove);
            window.removeEventListener('touchend', onUp);
        };
    }, [isDragging, updateFromClientX]);

    const onPointerDown = (e: React.MouseEvent | React.TouchEvent) => {
        setIsDragging(true);
        const x = 'touches' in e ? e.touches[0].clientX : e.clientX;
        updateFromClientX(x);
    };

    const onMouseMoveHover = (e: React.MouseEvent) => {
        if (isDragging) return;
        updateFromClientX(e.clientX);
    };

    const onKeyDown = (e: React.KeyboardEvent) => {
        const step = e.shiftKey ? 10 : 2;
        if (e.key === 'ArrowLeft') {
            e.preventDefault();
            setPosition((p) => Math.max(0, p - step));
        } else if (e.key === 'ArrowRight') {
            e.preventDefault();
            setPosition((p) => Math.min(100, p + step));
        } else if (e.key === 'Home') {
            e.preventDefault();
            setPosition(0);
        } else if (e.key === 'End') {
            e.preventDefault();
            setPosition(100);
        }
    };

    return (
        <section
            id="gallery"
            className="py-16 lg:py-24 bg-slate-50 dark:bg-navy-950"
        >
            <div className="container">
                <div className="text-center mb-16">
                    <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-navy dark:text-white font-serif leading-tight mb-6">
                        {(() => {
                            const title = t("gallery.title");
                            const word = t("gallery.highlightWord");
                            if (word && title.includes(word)) {
                                const idx = title.indexOf(word);
                                return (
                                    <>
                                        {title.slice(0, idx)}
                                        <span className="text-gold">{word}</span>
                                        {title.slice(idx + word.length)}
                                    </>
                                );
                            }
                            return title;
                        })()}
                    </h2>
                    <p className="text-lg text-slate-600 dark:text-slate-300 max-w-3xl mx-auto">
                        {t("gallery.subtitle")}
                    </p>
                </div>

                <Card className="max-w-4xl mx-auto border-slate-200 dark:border-slate-700 overflow-hidden">
                    <CardContent className="p-4 sm:p-8">
                        <div
                            ref={containerRef}
                            role="slider"
                            tabIndex={0}
                            aria-label={t("gallery.compareLabel") || "Drag to compare before and after"}
                            aria-valuemin={0}
                            aria-valuemax={100}
                            aria-valuenow={Math.round(position)}
                            onMouseDown={onPointerDown}
                            onTouchStart={onPointerDown}
                            onMouseMove={onMouseMoveHover}
                            onKeyDown={onKeyDown}
                            className="relative aspect-[4/3] rounded-xl overflow-hidden bg-slate-50 dark:bg-slate-800 sm:min-h-[400px] w-full select-none touch-none cursor-ew-resize focus:outline-none focus:ring-2 focus:ring-gold"
                        >
                            <OptimizedImage
                                src={current.after}
                                alt={`${t("gallery.after")} - ${current.description}`}
                                fill
                                sizes="(max-width: 1024px) 100vw, 896px"
                                className="object-cover pointer-events-none"
                            />

                            <div
                                className="absolute inset-0 overflow-hidden pointer-events-none"
                                style={{ clipPath: `inset(0 ${100 - position}% 0 0)` }}
                            >
                                <OptimizedImage
                                    src={current.before}
                                    alt={`${t("gallery.before")} - ${current.description}`}
                                    fill
                                    sizes="(max-width: 1024px) 100vw, 896px"
                                    className="object-cover pointer-events-none"
                                />
                            </div>

                            <div className="absolute top-3 left-3 bg-black/70 text-white text-xs font-semibold px-2 py-1 rounded pointer-events-none">
                                {t("gallery.before")}
                            </div>
                            <div className="absolute top-3 right-3 bg-gold text-navy text-xs font-semibold px-2 py-1 rounded pointer-events-none">
                                {t("gallery.after")}
                            </div>

                            <div
                                className="absolute top-0 bottom-0 w-0.5 bg-white shadow-[0_0_10px_rgba(0,0,0,0.4)] pointer-events-none"
                                style={{ left: `${position}%`, transform: 'translateX(-50%)' }}
                            />
                            <div
                                className="absolute top-1/2 w-10 h-10 rounded-full bg-white border-2 border-gold shadow-lg flex items-center justify-center pointer-events-none"
                                style={{ left: `${position}%`, transform: 'translate(-50%, -50%)' }}
                            >
                                <svg viewBox="0 0 24 24" className="w-5 h-5 text-navy" fill="currentColor" aria-hidden="true">
                                    <path d="M9 7l-5 5 5 5V7zm6 0v10l5-5-5-5z" />
                                </svg>
                            </div>

                            <div className="absolute bottom-4 left-4 right-4 pointer-events-none">
                                <div className="bg-black/50 backdrop-blur-sm rounded-lg p-4">
                                    <p className="text-white font-medium">
                                        {current.description}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>
        </section>
    );
}
